import ApiError from "@/api/ApiError";
import type { ApiAuthTokens } from "@/utils/token";
import { clearAuthTokens, getAccessToken, getRefreshToken, setAuthTokens } from "@/utils/token";
import { AuthRefreshResponse } from "./generated-api";

interface ApiArgs {
  basePath: string;
}

export interface ApiRequestOptions extends RequestInit {
  auth?: ApiAuthTokens;
}

const DEFAULT_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

class Api {
  basePath: string;
  private refreshPromises: Map<string, Promise<string | null>> = new Map();

  constructor({ basePath }: ApiArgs) {
    this.basePath = basePath;
  }

  private buildUrl(path: string): string {
    if (path.startsWith("http")) {
      return path;
    }

    if (!path.startsWith("/")) {
      throw new Error(`[ApiClient] Path must start with "/": ${path}`);
    }

    return `${this.basePath}/api/v1${path}`;
  }

  private static async parseResponseBody<T>(response: Response): Promise<T> {
    const text = await response.text();

    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      return { message: text } as T;
    }
  }

  private async refreshAccessToken(refreshTokenOverride?: string): Promise<string | null> {
    // В SSR можем передать refresh token явно, в браузере берём его из cookies.
    const refreshToken = refreshTokenOverride ?? getRefreshToken() ?? undefined;

    // Если refresh token отсутствует, сессию восстановить нельзя.
    if (!refreshToken) {
      await clearAuthTokens();
      return null;
    }

    // Дедупликация refresh-запросов по конкретному refresh token:
    // параллельные 401 для одного токена ждут один общий Promise.
    const existingRefreshPromise = this.refreshPromises.get(refreshToken);

    if (existingRefreshPromise) {
      return existingRefreshPromise;
    }

    const refreshPromise = (async () => {
      // Запрашиваем новую пару токенов у backend.
      const response = await fetch(this.buildUrl("/auth/refresh"), {
        method: "POST",
        headers: { ...DEFAULT_HEADERS },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        const body = await Api.parseResponseBody<{ message?: string }>(response);

        await clearAuthTokens();

        throw new ApiError(response.status, body.message || "Unable to refresh token");
      }

      const data = await Api.parseResponseBody<AuthRefreshResponse>(response);

      // Защита от некорректного payload в ответе refresh.
      if (!data.access_token || !data.refresh_token) {
        await clearAuthTokens();
        throw new ApiError(401, "Invalid refresh response payload");
      }

      // Сохраняем новые токены (в браузере это обновит cookies).
      setAuthTokens(data.access_token, data.refresh_token);

      // Возвращаем новый access token для повторного запроса.
      return data.access_token;
    })();

    // Сохраняем Promise, пока refresh-запрос выполняется.
    this.refreshPromises.set(refreshToken, refreshPromise);

    try {
      return await refreshPromise;
    } finally {
      // Всегда очищаем запись в map, даже при ошибке.
      this.refreshPromises.delete(refreshToken);
    }
  }

  private async request<T>(url: string, options?: ApiRequestOptions, isRetry = false): Promise<T> {
    // auth — опциональный SSR override токенов;
    // requestHeaders — заголовки от вызывающего кода.
    const { headers: requestHeaders, auth, ...requestInit } = options || {};

    // Начинаем с базовых API-заголовков.
    const headers = new Headers(DEFAULT_HEADERS);

    // Добавляем пользовательские заголовки поверх дефолтных.
    if (requestHeaders) {
      new Headers(requestHeaders).forEach((value, key) => headers.set(key, value));
    }

    // Приоритет токена:
    // 1) явно переданный auth из серверного кода, 2) токен из browser cookies.
    const accessToken = auth ? auth?.access_token : getAccessToken();

    // Добавляем Authorization только если токен существует.
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    if (typeof FormData !== "undefined" && requestInit.body instanceof FormData) {
      headers.delete("Content-Type");
    }

    // Пользовательские запросы не должны кэшироваться.
    // Для публичных оставляем revalidate по умолчанию.
    const isAuthRequest = Boolean(accessToken || auth?.refresh_token);

    const defaultCacheConfig = isAuthRequest ? { cache: "no-store" as const } : { next: { revalidate: 60 } };

    const response = await fetch(this.buildUrl(url), {
      ...requestInit,
      ...defaultCacheConfig,
      headers,
    });

    // При первой 401 пробуем один цикл refresh + retry.
    // При retry бесконечный цикл не допускаем.
    if (response.status === 401 && !isRetry && typeof window !== "undefined") {
      const refreshedAccessToken = await this.refreshAccessToken(auth?.refresh_token);

      if (refreshedAccessToken) {
        // Повторяем тот же запрос с новым access token, сохраняя method/body/headers.
        return this.request<T>(
          url,
          {
            ...requestInit,
            headers: requestHeaders,
            auth: { ...auth, access_token: refreshedAccessToken },
          },
          true,
        );
      }
    }

    // Преобразуем HTTP-ошибки в ApiError с сообщением backend, если оно есть.
    if (!response.ok) {
      const body = await Api.parseResponseBody<{ message?: string }>(response);
      throw new ApiError(response.status, body.message || "Request failed");
    }

    // Успешный payload парсим в ожидаемый generic-тип.
    return Api.parseResponseBody<T>(response);
  }

  async get<T>(url: string, params?: object, options?: ApiRequestOptions): Promise<T> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(param => searchParams.append(`${key}[]`, param));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
    }

    const queryString = searchParams.size ? `?${searchParams}` : "";

    return this.request<T>(`${url}${queryString}`, { method: "GET", ...options });
  }

  async post<T>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;

    return this.request<T>(url, {
      ...options,
      method: "POST",
      body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(url: string, data?: object, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(url: string, data?: object, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export default Api;
